/**
 * Ring buffer for reconciliation event diagnostics.
 *
 * Module-level singleton (same pattern as FrameTimingBuffer).
 * Pre-allocated array of ReconciliationEvent objects overwritten in-place
 * (zero allocation per event in recordReconciliation).
 *
 * Used to diagnose client/server simulation divergence (rubber-banding, desync).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ReconciliationEvent {
  timestamp: number;
  serverTick: number;
  clientTickBefore: number;
  clientTickAfter: number;
  pendingInputCount: number;

  // Ship state comparison
  serverShipX: number;
  serverShipY: number;
  serverShipVx: number;
  serverShipVy: number;
  serverShipRotation: number;
  serverShipBattery: number;

  clientShipBeforeX: number;
  clientShipBeforeY: number;
  clientShipBeforeVx: number;
  clientShipBeforeVy: number;
  clientShipBeforeRotation: number;
  clientShipBeforeBattery: number;

  reconciledShipX: number;
  reconciledShipY: number;
  reconciledShipVx: number;
  reconciledShipVy: number;
  reconciledShipRotation: number;
  reconciledShipBattery: number;

  desyncDistance: number;
  reconciledDistance: number;
  reconcileMs: number;

  // Moon state
  serverMoonAngle: number;
  extrapolatedMoonAngle: number;

  // Input pipeline
  lastAckedInput: number;
  oldestPendingSeq: number;
  newestPendingSeq: number;
}

export interface ReconciliationStats {
  eventCount: number;
  avgDesyncDistance: number;
  maxDesyncDistance: number;
  avgReconciledDistance: number;
  avgPendingInputs: number;
  avgReconcileMs: number;
  lastEventTimestamp: number;
  lastDesyncDistance: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const RECONCILIATION_BUFFER_SIZE = 100;

// ---------------------------------------------------------------------------
// Module state (singleton)
// ---------------------------------------------------------------------------

let buffer: ReconciliationEvent[] = [];
let writeIndex = 0;
let eventCount = 0;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pre-allocate the ring buffer and reset counters.
 * Call once at game loop start.
 */
export function initReconciliationLog(): void {
  buffer = new Array<ReconciliationEvent>(RECONCILIATION_BUFFER_SIZE);
  for (let i = 0; i < RECONCILIATION_BUFFER_SIZE; i++) {
    buffer[i] = {
      timestamp: 0,
      serverTick: 0,
      clientTickBefore: 0,
      clientTickAfter: 0,
      pendingInputCount: 0,
      serverShipX: 0, serverShipY: 0, serverShipVx: 0, serverShipVy: 0,
      serverShipRotation: 0, serverShipBattery: 0,
      clientShipBeforeX: 0, clientShipBeforeY: 0, clientShipBeforeVx: 0, clientShipBeforeVy: 0,
      clientShipBeforeRotation: 0, clientShipBeforeBattery: 0,
      reconciledShipX: 0, reconciledShipY: 0, reconciledShipVx: 0, reconciledShipVy: 0,
      reconciledShipRotation: 0, reconciledShipBattery: 0,
      desyncDistance: 0,
      reconciledDistance: 0,
      reconcileMs: 0,
      serverMoonAngle: 0,
      extrapolatedMoonAngle: 0,
      lastAckedInput: 0,
      oldestPendingSeq: 0,
      newestPendingSeq: 0,
    };
  }
  writeIndex = 0;
  eventCount = 0;
}

/**
 * Record one reconciliation event. Zero allocations — overwrites in-place.
 */
export function recordReconciliation(event: ReconciliationEvent): void {
  if (buffer.length === 0) return;

  const slot = buffer[writeIndex];
  slot.timestamp = event.timestamp;
  slot.serverTick = event.serverTick;
  slot.clientTickBefore = event.clientTickBefore;
  slot.clientTickAfter = event.clientTickAfter;
  slot.pendingInputCount = event.pendingInputCount;

  slot.serverShipX = event.serverShipX;
  slot.serverShipY = event.serverShipY;
  slot.serverShipVx = event.serverShipVx;
  slot.serverShipVy = event.serverShipVy;
  slot.serverShipRotation = event.serverShipRotation;
  slot.serverShipBattery = event.serverShipBattery;

  slot.clientShipBeforeX = event.clientShipBeforeX;
  slot.clientShipBeforeY = event.clientShipBeforeY;
  slot.clientShipBeforeVx = event.clientShipBeforeVx;
  slot.clientShipBeforeVy = event.clientShipBeforeVy;
  slot.clientShipBeforeRotation = event.clientShipBeforeRotation;
  slot.clientShipBeforeBattery = event.clientShipBeforeBattery;

  slot.reconciledShipX = event.reconciledShipX;
  slot.reconciledShipY = event.reconciledShipY;
  slot.reconciledShipVx = event.reconciledShipVx;
  slot.reconciledShipVy = event.reconciledShipVy;
  slot.reconciledShipRotation = event.reconciledShipRotation;
  slot.reconciledShipBattery = event.reconciledShipBattery;

  slot.desyncDistance = event.desyncDistance;
  slot.reconciledDistance = event.reconciledDistance;
  slot.reconcileMs = event.reconcileMs;

  slot.serverMoonAngle = event.serverMoonAngle;
  slot.extrapolatedMoonAngle = event.extrapolatedMoonAngle;

  slot.lastAckedInput = event.lastAckedInput;
  slot.oldestPendingSeq = event.oldestPendingSeq;
  slot.newestPendingSeq = event.newestPendingSeq;

  eventCount++;
  writeIndex = (writeIndex + 1) % RECONCILIATION_BUFFER_SIZE;
}

/**
 * Return chronological array of all recorded events.
 * Allocates — only call at export time.
 */
export function getReconciliationLog(): ReconciliationEvent[] {
  const validCount = Math.min(eventCount, RECONCILIATION_BUFFER_SIZE);
  if (validCount === 0) return [];

  const result: ReconciliationEvent[] = [];
  if (eventCount <= RECONCILIATION_BUFFER_SIZE) {
    for (let i = 0; i < validCount; i++) {
      result.push({ ...buffer[i] });
    }
  } else {
    // Buffer has wrapped — read from writeIndex (oldest) forward
    for (let i = 0; i < RECONCILIATION_BUFFER_SIZE; i++) {
      result.push({ ...buffer[(writeIndex + i) % RECONCILIATION_BUFFER_SIZE] });
    }
  }
  return result;
}

/**
 * Return summary stats for DebugPanel display.
 * Cheap — iterates buffer but does not allocate per-event.
 */
export function getReconciliationStats(): ReconciliationStats {
  const validCount = Math.min(eventCount, RECONCILIATION_BUFFER_SIZE);
  if (validCount === 0) {
    return {
      eventCount: 0,
      avgDesyncDistance: 0,
      maxDesyncDistance: 0,
      avgReconciledDistance: 0,
      avgPendingInputs: 0,
      avgReconcileMs: 0,
      lastEventTimestamp: 0,
      lastDesyncDistance: 0,
    };
  }

  let sumDesync = 0;
  let maxDesync = 0;
  let sumReconciled = 0;
  let sumPending = 0;
  let sumMs = 0;

  const startIdx = eventCount <= RECONCILIATION_BUFFER_SIZE ? 0 : writeIndex;
  for (let i = 0; i < validCount; i++) {
    const slot = buffer[(startIdx + i) % RECONCILIATION_BUFFER_SIZE];
    sumDesync += slot.desyncDistance;
    if (slot.desyncDistance > maxDesync) maxDesync = slot.desyncDistance;
    sumReconciled += slot.reconciledDistance;
    sumPending += slot.pendingInputCount;
    sumMs += slot.reconcileMs;
  }

  // Last event is the one before writeIndex
  const lastIdx = (writeIndex - 1 + RECONCILIATION_BUFFER_SIZE) % RECONCILIATION_BUFFER_SIZE;
  const last = buffer[lastIdx];

  return {
    eventCount,
    avgDesyncDistance: sumDesync / validCount,
    maxDesyncDistance: maxDesync,
    avgReconciledDistance: sumReconciled / validCount,
    avgPendingInputs: sumPending / validCount,
    avgReconcileMs: sumMs / validCount,
    lastEventTimestamp: last.timestamp,
    lastDesyncDistance: last.desyncDistance,
  };
}
